import React, { useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import { colors, css, fonts, frames, getGraphColor } from "../../../Styles";
import { LineSegment, VictoryAxis, VictoryBar, VictoryChart, VictoryGroup, VictoryLabel, VictoryStack, VictoryTooltip } from "victory";
import { Animal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";
import { useAnimalConsumptionByMultipleIds } from "../../../Services/Agrisys_GqlDataCaller/ConsumptionCalls/consumptionSlice";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../Store";
import { Graph_QL, useAnimal, useFeedComponents } from "../../../Services";

const _feedCurveWidth = 600;
const _chartWidth = 550;
const _chartHeight = 300;
const _fontSize = { ...fonts.fontSize4 };
const _primaryColor = colors.greyPrimary;

interface DayData {
  dayOffset: number;
  date: Date;
  components: {
    componentId: number;
    expected: number;
    consumed: number;
  }[];
}

const formatDate = (date: Date) => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  return `${day}/${month}`;
};

interface TenDaysGraph_Props {
  data: Animal[];
}

export default function TenDaysGraphComponents({ data }: TenDaysGraph_Props) {
  const { t } = useTranslation();
  const [hoveredBar, setHoveredBar] = useState<{ comp: number; x: string; type: "expected" | "consumed" } | null>(null);
  const [hoveredComponent, setHoveredComponent] = useState<number | null>(null);
  
  const today = new Date();
  const grouped = new Map<number, Map<number, { expected: number; consumed: number }>>(); 

  // #region | GraphQL
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const domain = useMemo(()=> (Graph_QL.ObjToString({domain: farm ?? ""})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({ domain: farm ?? "" })), [farm]);
  
  const { call: getFeedComponentsInfo, data: feedComponentsState } = useFeedComponents(connection);
  
  useEffect(() => {
    getFeedComponentsInfo({fetchAmount: 50}, connection);
  }, [connection]);

  const feedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data: undefined,[domain, feedComponentsState])
  // #endregion

  // #region | Data
const consumptionEntities = useAnimalConsumptionByMultipleIds(data.map(a => a.animal_id.toString()))
  
  const consumptionsArray = Object.values(consumptionEntities).filter((entity) => !entity.isLoading && entity.entity !== undefined).map((entityConsumption) => entityConsumption.entity?.consumptions)
  consumptionsArray.filter(Boolean).flat().forEach(consumption => {
    const offset = Math.abs(consumption?.milestoneDayOffset ?? 0);

    const rawComponentId = consumption?.componentId;
    if (rawComponentId === 1) return;
    const componentId = rawComponentId == null ? -1 : rawComponentId;

    if (!grouped.has(offset)) { grouped.set(offset, new Map()) }

    const dayMap = grouped.get(offset)!;

    if (!dayMap.has(componentId)) { dayMap.set(componentId, { expected: 0, consumed: 0 }) }

    const entry = dayMap.get(componentId)!;
    entry.expected += consumption?.total ?? 0;
    entry.consumed += consumption?.progressed ?? 0;
  });
  
  const last10Days: DayData[] = Array.from({ length: 10 }, (_, i) => {
    const offset = 9 - i;
    const dayMap = grouped.get(offset) ?? new Map();
  
    return {
      dayOffset: offset,
      date: new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - offset
      ),
      components: Array.from(dayMap.entries()).map(
        ([componentId, data]) => ({
          componentId,
          expected: data.expected,
          consumed: data.consumed,
        })
      ),
    };
  });
  
  const tickValues = last10Days.map(day => formatDate(day.date));
  const componentIds = Array.from(new Set(last10Days.flatMap(day => day.components.map(c => c.componentId))));    
    
  const totalDataPerComponent = componentIds.map(componentId =>
    last10Days.map(day => {
      const comp = day.components.find(c => c.componentId === componentId);
  
      return {
        x: formatDate(day.date),
        y: comp?.expected ?? 0,
        comp: comp?.componentId,
        type: "expected"
      };
    })
  );
  
  const progressedDataPerComponent = componentIds.map(componentId =>
    last10Days.map(day => {
      const comp = day.components.find(c => c.componentId === componentId);
      
      return {
        x: formatDate(day.date),
        y: comp?.consumed ?? 0,
        comp: comp?.componentId,
        type: "consumed"
      };
    })
  );
  // #endregion
  
  // #region | Graph Width
  // To achieve this, I had to reset svg overflow to "visible" in reset.css // MWE
  const maxValue = Math.max(  ...last10Days.map(day => day.components.reduce((sum, c) => sum + c.expected, 0)));
  const feedTotalRange = [0, maxValue / 1000];

  const longestNumber = feedTotalRange.reduce((maxNumber, currentNumber) => {
    return Math.round(currentNumber) > Math.round(maxNumber)
      ? Math.round(currentNumber)
      : Math.round(maxNumber);
  }, Math.round(feedTotalRange[0]));

  const longestNumberLenght = Number(longestNumber.toString().replace(/[.,]/g, '').length)

  const _paddingLeft = longestNumberLenght <= 2 ? 0 : ((longestNumberLenght - 2) * 8);
  // #endregion
  
  // #region | Axes
  // Y-Axis
  const yAxis = (
    <VictoryAxis
      dependentAxis
      offsetX={32}
      standalone={false}
      crossAxis={false}
      tickFormat={(t:number) => (t / 1000)}
      label={t("units.kg", { defaultValue: "KG" })}
      style={{
        ticks: { stroke: _primaryColor, size: 5 },
        axis: { stroke: _primaryColor, strokeWidth: 2 },
      }}
      tickLabelComponent={
        <VictoryLabel
          textAnchor="end"
          style={{ ...fonts.paragraph, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisLabelComponent={
        <VictoryLabel
          angle={0}
          verticalAnchor="start" 
          textAnchor="start"
          x={0}
          y={0}
          style={{ ...fonts.header, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisComponent={
        <LineSegment
          y1={0}
          y2={_chartHeight - 20}
        />
      }
    />
  )
  
  // X-Axis
  const xAxis = (
    <VictoryAxis
      offsetY={25}
      orientation="bottom"
      standalone={false}
      crossAxis={false}
      tickValues={tickValues}
      label={t("units.date", {defaultValue: "Date"})}
      style={{
        ticks: { stroke: _primaryColor, size: 5 },
        axis: { stroke: _primaryColor, strokeWidth: 2 },
      }}
      tickLabelComponent={
        <VictoryLabel
          y={_chartHeight}
          verticalAnchor="end"
          style={{ ...fonts.paragraph, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisLabelComponent={
        <VictoryLabel
          y={_chartHeight}
          x={_chartWidth}
          verticalAnchor="end"
          textAnchor="end"
          style={{ ...fonts.header, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisComponent={
        <LineSegment x1={27} x2={_chartWidth} />
      }
    />
  )
  // #endregion
      
  // #region | Tooltip
  const tooltip = (
    <VictoryTooltip
      dy={-3}
      cornerRadius={8}
      pointerLength={6}
      pointerWidth={12}
      flyoutPadding={{ top: 1, bottom: 1, left: 6, right: 6 }}
      flyoutStyle={{
        fill: colors.greyDarkPrimary,
        stroke: 0
      }}
      style={{
        fill: colors.greyLightTertiary,
        fontSize: "11px",
        ...fonts.paragraph,
      }}
    />
  )
  // #endregion
  
  // #region | Bars
  const feedTotalBars = (
    <VictoryStack>
      {totalDataPerComponent.map((data, i) => { return (
        <VictoryBar
          key={`expected-${i}`}
          events={[{ target: "data", eventHandlers: {
            onMouseOver: (_, props) => {
              setHoveredBar({ comp: props.datum.comp, x: props.datum.x, type: props.datum.type });
              return [{ target: "labels", mutation: () => ({ active: true }) }];
            },
            onMouseOut: () => {
              setHoveredBar(null);
              return [{ target: "labels", mutation: () => ({ active: false }) }];
            }
          }}]}
          data={data}
          style={{ data: {
            width: 10,
            fill: ({ datum }) => {
              if (hoveredComponent !== null) { return datum.comp === hoveredComponent ? colors.greyTertiary : getGraphColor(datum.comp ?? 0).graphColor }
              if (hoveredBar) { return ( datum.comp === hoveredBar.comp && datum.x === hoveredBar.x && hoveredBar.type === "expected" ) ? colors.greyTertiary : getGraphColor(datum.comp ?? 0).graphColor }
              return getGraphColor(datum.comp ?? 0).graphColor;
            },
            transition: "0.3s",
          }}}
          labels={({ datum }) => `${t("feed.general.expectedConsumption", { defaultValue: "Expected Consumption" })}: ${(datum.y / 1000).toFixed(2)} kg`}
          labelComponent={tooltip}
        />
      )})}
    </VictoryStack>
  );

  const feedConsumptionBars = (
    <VictoryStack>
      {progressedDataPerComponent.map((data, i) => { return (
        <VictoryBar
          key={`consumed-${i}`}
          data={data}
          events={[{ target: "data", eventHandlers: {
            onMouseOver: (_, props) => {
              setHoveredBar({ comp: props.datum.comp, x: props.datum.x, type: props.datum.type });
              return [{ target: "labels", mutation: () => ({ active: true }) }];
            },
            onMouseOut: () => {
              setHoveredBar(null);
              return [{ target: "labels", mutation: () => ({ active: false }) }];
            }
          }}]}
          style={{ data: {
            width: 10,
            fill: ({ datum }) => {
              if (hoveredComponent !== null) { return datum.comp === hoveredComponent ? colors.greyTertiary : getGraphColor(datum.comp ?? 0).graphColor }
              if (hoveredBar) { return ( datum.comp === hoveredBar.comp && datum.x === hoveredBar.x && hoveredBar.type === "consumed" ) ? colors.greyTertiary : getGraphColor(datum.comp ?? 0).graphColor }
              return getGraphColor(datum.comp ?? 0).graphColor;
            },
            transition: "0.3s",
          }}}
          labels={({ datum }) => `${t("feed.general.actualConsumption", { defaultValue: "Actual Consumption" })}: ${(datum.y / 1000).toFixed(2)} kg`}
          labelComponent={tooltip}
        />
      )})}
    </VictoryStack>
  );
  // #endregion

  const filteredFeedComponents = useMemo(() => {
    if (!feedComponents) return [];
    return feedComponents.filter(fc => componentIds.includes(fc.id));
  }, [feedComponents, componentIds]);

  const hoveredBarTotal = useMemo(() => {
    if (!hoveredBar) return 0;
  
    const day = last10Days.find(d => formatDate(d.date) === hoveredBar.x);
    if (!day) return 0;
  
    if (hoveredBar.type === "expected") {
      return day.components.reduce((sum, c) => sum + c.expected, 0);
    }
  
    return day.components.reduce((sum, c) => sum + c.consumed, 0);
  }, [hoveredBar, last10Days]);

  const formatFullDate = (dateString?: string) => {
    if (!dateString) return;
    const [day, month] = dateString.split("/").map(Number);
  
    const date = new Date(new Date().getFullYear(), month - 1, day);
  
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    });
  };

  return (
    consumptionsArray.length > 0 ? <div style={{ position: "relative", width: _feedCurveWidth, display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", paddingLeft: _paddingLeft }}>
      <VictoryChart padding={{ top: 40, bottom: 25, left: 45, right: 40 }} width={_chartWidth} height={_chartHeight} domainPadding={{ x: 20 }}>
        <VictoryGroup offset={14}>
          {feedTotalBars}
          {feedConsumptionBars}
        </VictoryGroup>
        {yAxis}
        {xAxis}
      </VictoryChart>
      <div style={{display: "flex", flexWrap: "wrap", gap: "10px"}}>
        {filteredFeedComponents.map(comp => (
          <frames.GridLabel
            $component={comp}
            $isHovered={comp.id === hoveredBar?.comp}
            onMouseEnter={() => setHoveredComponent(comp.id)}
            onMouseLeave={() => setHoveredComponent(null)}
            $width='fit-content'
          >
            {comp.name}
          </frames.GridLabel>
        ))}
        {componentIds.includes(-1) && (
          <frames.GridLabel
            $isHovered={hoveredBar?.comp === -1}
            onMouseEnter={() => setHoveredComponent(-1)}
            onMouseLeave={() => setHoveredComponent(null)}
            $width='fit-content'
          >
            {t("unknown", {defaultValue: "unknown"})}
          </frames.GridLabel>
        )}
      </div>
      <div style={{ position: "absolute", top: "-5px", left: "40px", display: "flex", gap: "5px", opacity: hoveredBar ? "1" : "0", transition: "0.3s" }}>
        { hoveredBar && <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyDarkPrimary, lineHeight: "1.5" }}>{`${formatFullDate(hoveredBar?.x)}`}</p> }
        { hoveredBar && <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>{`
          ${hoveredBar?.type === "expected" ? t("feed.general.expectedConsumption", { defaultValue: "Expected Consumption" }) : t("feed.general.actualConsumption", { defaultValue: "Actual Consumption" })} ${t("units.total", {defaultValue: "total"})}:
          ${hoveredBarTotal/1000} kg
        `}</p> }
      </div>
    </div> : <p style={{...css.emptyPen, opacity: "0.5" }}>{t("noData", {defaultValue: "no data"})}</p>
  );
}